import { Stack, Switch, Typography } from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  label: React.ReactNode;
  hint?: React.ReactNode;
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Popis pod menovkou namiesto vpravo - mobilné rozloženie. */
  stacked?: boolean;
  /** Ohraničený blok s podkladom namiesto holého riadku. */
  framed?: boolean;
  /**
   * Voľba, ktorú tento nález nemá. Riadok zostáva na obrazovke aj s popisom,
   * ktorý hovorí prečo - schovať ho by znamenalo, že úradník sa pýta, kam sa
   * podelo prijatie.
   */
  disabled?: boolean;
};

/**
 * Čierny prepínač. Zostal **len prepínaču nevhodnosti** v hlavičke stránky
 * ([ReportVisibilityToggle](ReportVisibilityToggle.tsx)): ten nie je súčasťou
 * rozhodovania o náleze, je to príznak podnetu, a v hlavičke stojí sám.
 */
export const BLACK_SWITCH_SX = {
  flex: 'none',
  '& .MuiSwitch-track': {
    backgroundColor: 'grey.300',
    opacity: 1,
  },
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: 'common.white',
    '& + .MuiSwitch-track': {
      backgroundColor: 'grey.800',
      opacity: 1,
    },
  },
};

/**
 * Prepínač rozhodnutia je **primary**, nie čierny.
 *
 * Čierny bol dovtedy preto, aby červená zostala jedinému tlačidlu v pätičke.
 * V karte nálezu to ale prestalo platiť: kariet je pod sebou niekoľko, každá
 * má vlastnú pätičku, a tmavé prepínače v nich splývali s tmavou hlavičkou
 * karty. Zapnutá voľba je odteraz to isté, čo vybraný `DecisionChip` vedľa -
 * jedna farba pre „toto som zvolil".
 */
export const PRIMARY_SWITCH_SX = {
  flex: 'none',
  '& .MuiSwitch-track': {
    backgroundColor: 'grey.300',
    opacity: 1,
  },
  '& .MuiSwitch-switchBase.Mui-checked': {
    color: 'common.white',
    '& + .MuiSwitch-track': {
      backgroundColor: 'primary.main',
      opacity: 1,
    },
  },
};

/**
 * Prepínač rozhodnutia: prepínač **vľavo**, menovka, popis vpravo (na mobile
 * pod menovkou).
 */
const DecisionSwitch: React.FC<Props> = ({
  label,
  hint,
  checked,
  onChange,
  stacked = false,
  framed = false,
  disabled = false,
}) => (
  <Stack
    component='label'
    direction='row'
    alignItems='center'
    gap={1.375}
    sx={{
      cursor: disabled ? 'default' : 'pointer',
      // Menovka aj popis ostávajú čitateľné - stlmuje sa len natoľko, aby bolo
      // vidieť, že sa riadok neovláda.
      ...(disabled && { opacity: 0.6 }),
      ...(framed && {
        px: 1.375,
        py: 1.125,
        borderRadius: 0.75,
        border: (theme) => `1px solid ${theme.palette.divider}`,
        backgroundColor: 'grey.50',
      }),
    }}
  >
    <Switch
      checked={checked}
      disabled={disabled}
      onChange={(_event, value) => onChange(value)}
      sx={PRIMARY_SWITCH_SX}
    />

    {stacked ? (
      <Stack sx={{ minWidth: 0 }}>
        <Typography variant='body2' fontWeight={500}>
          {label}
        </Typography>
        {hint && (
          <Typography variant='caption' color='text.secondary'>
            {hint}
          </Typography>
        )}
      </Stack>
    ) : (
      <>
        <Typography variant='body2' fontWeight={500}>
          {label}
        </Typography>
        {hint && (
          <Typography
            variant='caption'
            color='text.secondary'
            sx={{ ml: 'auto', textAlign: 'right' }}
          >
            {hint}
          </Typography>
        )}
      </>
    )}
  </Stack>
);

export default DecisionSwitch;
